<script setup>
import { ref } from 'vue'
import { useUserStore } from '../stores/userStore'

const props = defineProps({
  show: Boolean
})

const emit = defineEmits(['close'])
const userStore = useUserStore()

const newTrader = ref({
  name: '',
  email: '',
  role: 'trader',
  status: 'active',
  workLocation: 'office', // 'office' or 'remote'
  accessLevel: 'basic',
  tradingPermissions: {
    createMarkets: false,
    editOdds: false,
    publishOdds: false,
    manageSettlement: false,
    viewLogs: false,
    manageProjections: false,
    viewBalancedLines: false,
    viewMilestones: false
  },
  projectionAccess: {
    points: false,
    rebounds: false,
    assists: false,
    threePointers: false,
    pra: false,
    turnovers: false,
    blocks: false,
    steals: false
  },
  marketTypes: {
    preMatch: true,
    live: false,
    futures: false
  },
  riskManagement: {
    maxStake: 1000,
    maxLiability: 5000,
    suspendThreshold: 0.1,
    autoSuspend: true
  },
  webConsole: {
    enabled: true,
    twoFactorAuth: true,
    allowedIPs: [],
    workingHours: {
      start: '09:00',
      end: '17:00',
      timezone: 'UTC'
    }
  }
})

const tradingPermissions = [
  { id: 'createMarkets', label: 'Create Markets', description: 'Ability to create new betting markets' },
  { id: 'editOdds', label: 'Edit Odds', description: 'Can modify odds and lines' },
  { id: 'publishOdds', label: 'Publish Odds', description: 'Authority to publish odds to live system' },
  { id: 'manageSettlement', label: 'Manage Settlement', description: 'Can settle and void bets' },
  { id: 'viewLogs', label: 'View Logs', description: 'Access to system and trading logs' },
  { id: 'manageProjections', label: 'Manage Projections', description: 'Create and edit player projections' },
  { id: 'viewBalancedLines', label: 'View Balanced Lines', description: 'Access to balanced lines data' },
  { id: 'viewMilestones', label: 'View Milestones', description: 'Access to milestone tracking' }
]

const projectionTypes = [
  { id: 'points', label: 'Points', description: 'Player points projections' },
  { id: 'rebounds', label: 'Rebounds', description: 'Player rebounds projections' },
  { id: 'assists', label: 'Assists', description: 'Player assists projections' },
  { id: 'threePointers', label: '3 Pointers', description: 'Three point shots projections' },
  { id: 'pra', label: 'PRA', description: 'Points + Rebounds + Assists projections' },
  { id: 'turnovers', label: 'Turnovers', description: 'Player turnovers projections' },
  { id: 'blocks', label: 'Blocks', description: 'Player blocks projections' },
  { id: 'steals', label: 'Steals', description: 'Player steals projections' }
]

const createTrader = () => {
  userStore.addUser({
    id: Date.now(),
    ...newTrader.value,
    apiKey: `ak_live_****${Math.random().toString(36).substr(2, 4)}`,
    lastActive: new Date().toISOString(),
    createdAt: new Date().toISOString().split('T')[0]
  })
  emit('close')
  // Reset form...
}
</script>

<template>
  <div v-if="show" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg w-[800px] max-h-[90vh] overflow-y-auto">
      <div class="p-6 border-b flex justify-between items-center">
        <h2 class="text-xl font-semibold">Add New Trader</h2>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="p-6">
        <form @submit.prevent="createTrader">
          <div class="space-y-6">
            <!-- Basic Information -->
            <div class="grid grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <input 
                  v-model="newTrader.name"
                  type="text" 
                  required
                  class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
                  placeholder="Enter trader name"
                >
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input 
                  v-model="newTrader.email"
                  type="email" 
                  required
                  class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
                  placeholder="Enter email address"
                >
              </div>
            </div>

            <!-- Work Location -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Work Location</label>
              <div class="flex gap-4">
                <label class="flex items-center">
                  <input 
                    type="radio" 
                    v-model="newTrader.workLocation" 
                    value="office"
                    class="form-radio text-pink-500"
                  >
                  <span class="ml-2">Office</span>
                </label>
                <label class="flex items-center">
                  <input 
                    type="radio" 
                    v-model="newTrader.workLocation" 
                    value="remote"
                    class="form-radio text-pink-500"
                  >
                  <span class="ml-2">Remote</span>
                </label>
              </div>
            </div>

            <!-- Trading Permissions -->
            <div class="bg-gray-50 rounded-lg p-4">
              <h4 class="font-medium mb-3">Trading Permissions</h4>
              <div class="space-y-4">
                <div 
                  v-for="perm in tradingPermissions" 
                  :key="perm.id"
                  class="flex items-center justify-between p-3 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                  @click="newTrader.tradingPermissions[perm.id] = !newTrader.tradingPermissions[perm.id]"
                >
                  <div>
                    <label class="font-medium text-sm select-none">{{ perm.label }}</label>
                    <p class="text-sm text-gray-500 select-none">{{ perm.description }}</p>
                  </div>
                  <div class="relative inline-block w-12 h-6">
                    <input 
                      type="checkbox" 
                      v-model="newTrader.tradingPermissions[perm.id]"
                      class="sr-only peer"
                      @click.stop
                    >
                    <div 
                      class="toggle-bg block bg-gray-200 rounded-full h-6 w-12 cursor-pointer 
                             transition-colors duration-200 ease-in-out
                             peer-checked:bg-pink-500"
                    ></div>
                    <div 
                      class="toggle-dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full shadow
                             transition-transform duration-200 ease-in-out
                             peer-checked:translate-x-6"
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Projection Types Access -->
            <div class="bg-gray-50 rounded-lg p-4">
              <h4 class="font-medium mb-3">Projection Types Access</h4>
              <div class="space-y-4">
                <div 
                  v-for="proj in projectionTypes" 
                  :key="proj.id"
                  class="flex items-center justify-between p-3 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                  @click="newTrader.projectionAccess[proj.id] = !newTrader.projectionAccess[proj.id]"
                >
                  <div>
                    <label class="font-medium text-sm select-none">{{ proj.label }}</label>
                    <p class="text-sm text-gray-500 select-none">{{ proj.description }}</p>
                  </div>
                  <div class="relative inline-block w-12 h-6">
                    <input 
                      type="checkbox" 
                      v-model="newTrader.projectionAccess[proj.id]"
                      class="sr-only peer"
                      @click.stop
                    >
                    <div 
                      class="toggle-bg block bg-gray-200 rounded-full h-6 w-12 cursor-pointer 
                             transition-colors duration-200 ease-in-out
                             peer-checked:bg-pink-500"
                    ></div>
                    <div 
                      class="toggle-dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full shadow
                             transition-transform duration-200 ease-in-out
                             peer-checked:translate-x-6"
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Risk Management -->
            <div class="bg-gray-50 rounded-lg p-4">
              <h4 class="font-medium mb-3">Risk Management</h4>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Max Stake</label>
                  <input 
                    v-model="newTrader.riskManagement.maxStake"
                    type="number" 
                    class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
                  >
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Max Liability</label>
                  <input 
                    v-model="newTrader.riskManagement.maxLiability"
                    type="number" 
                    class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
                  >
                </div>
              </div>
            </div>

            <!-- Working Hours -->
            <div class="bg-gray-50 rounded-lg p-4">
              <h4 class="font-medium mb-3">Working Hours</h4>
              <div class="grid grid-cols-3 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                  <input 
                    v-model="newTrader.webConsole.workingHours.start"
                    type="time" 
                    class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
                  >
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                  <input 
                    v-model="newTrader.webConsole.workingHours.end"
                    type="time" 
                    class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
                  >
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
                  <select 
                    v-model="newTrader.webConsole.workingHours.timezone"
                    class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
                  >
                    <option value="UTC">UTC</option>
                    <option value="EST">EST</option>
                    <option value="CET">CET</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      <div class="p-6 border-t bg-gray-50 flex justify-end gap-3">
        <button 
          @click="$emit('close')"
          class="px-4 py-2 border rounded-lg hover:bg-gray-100"
        >
          Cancel
        </button>
        <button 
          @click="createTrader"
          class="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600"
        >
          Create Trader
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.toggle-bg {
  position: relative;
}

.toggle-bg:before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  border-radius: 9999px;
  background-color: transparent;
  transition: background-color 0.2s;
}

.toggle-bg:hover:before {
  background-color: rgba(236, 72, 153, 0.1);
}

.toggle-dot {
  transform: translateX(0);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Add ripple effect on click */
@keyframes ripple {
  0% {
    transform: scale(0);
    opacity: 1;
  }
  100% {
    transform: scale(2.5);
    opacity: 0;
  }
}

.toggle-bg:active:before {
  animation: ripple 0.5s ease-out;
}

/* Improve hover states */
.hover\:bg-gray-100:hover {
  background-color: rgba(243, 244, 246, 0.8);
}

/* Smooth transitions */
.transition-transform {
  transition-property: transform;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 200ms;
}

.transition-colors {
  transition-property: background-color, border-color;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 200ms;
}

/* Focus styles */
input:focus + .toggle-bg {
  outline: 2px solid transparent;
  outline-offset: 2px;
  box-shadow: 0 0 0 2px #fff, 0 0 0 4px #EC4899;
}

/* Active state enhancements */
.peer:checked + .toggle-bg {
  background-color: #EC4899;
}

.peer:checked + .toggle-bg .toggle-dot {
  transform: translateX(100%);
}

/* Disabled state */
.peer:disabled + .toggle-bg {
  opacity: 0.5;
  cursor: not-allowed;
}
</style> 